import { useSyncExternalStore } from "react";

// Il microfono e l'ascolto delle chiamate (richiesta del product owner del 6 ottobre 2026: «Non vorrei che utilizziamo
// quello del computer invece di quello delle cuffie»; il posto l'ha scelto lui: «Nella postazione»). La scelta resta su
// questo computer (`localStorage`, come la rail in `shell/rail-mode.ts`), una per tutta l'applicazione e non per azienda, e
// vale per ogni chiamata della postazione, Power Dialer compreso: il telefono del browser la passa al SDK prima di comporre
// (`twilio-phone.ts`) e la sessione la porta sulla chiamata in corso quando cambia (`call-session.tsx`). `null` vuol dire
// «Predefinito di sistema», cioè nessuna scelta. Un dispositivo scelto che manca non ferma mai una chiamata: si usa quello
// di sistema (regola dei pochi blocchi del 5 ottobre 2026), e la postazione lo dice (`leads/card/AudioDevices.tsx`).

/** I dispositivi scelti, con il loro `deviceId` del browser; `null`: quello di sistema. */
export type AudioChoice = { input: string | null; output: string | null };

/** Nessuna scelta: microfono e ascolto di sistema. */
export const SYSTEM_AUDIO: AudioChoice = { input: null, output: null };

/** Nel `localStorage` del computer, come JSON: `{"input": …, "output": …}`. */
export const AUDIO_CHOICE_KEY = "gioia365.audio";

/** Le voci del browser che non sono un dispositivo ma «quello di sistema» (Chrome): non si offrono nelle tendine. */
export const SYSTEM_DEVICE_IDS: ReadonlySet<string> = new Set(["default", "communications"]);

/**
 * Le parole che, nel nome del microfono, dicono quello del computer (maiuscole o minuscole). Servono soltanto al
 * suggerimento gentile della postazione, mai a fermare una chiamata.
 */
export const BUILT_IN_MICROPHONE_WORDS = ["built-in", "integrat", "interno", "internal", "macbook"] as const;

/** Quanti caratteri al massimo del nome di un dispositivo nel pulsante della postazione. */
const SHORT_NAME_LENGTH = 36;

/** Il `localStorage` del computer, oppure `null` se il browser lo nega. */
function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function rawChoice(storage: Storage | null): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(AUDIO_CHOICE_KEY);
  } catch {
    return null;
  }
}

const validId = (value: unknown): value is string | null => value === null || (typeof value === "string" && value !== "");

function parseChoice(raw: string | null): AudioChoice {
  if (raw === null) return SYSTEM_AUDIO;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null || Array.isArray(value)) return SYSTEM_AUDIO;
    const { input, output } = value as { input?: unknown; output?: unknown };
    return validId(input) && validId(output) ? { input, output } : SYSTEM_AUDIO;
  } catch {
    return SYSTEM_AUDIO;
  }
}

/** La scelta ricordata; quella di sistema con un valore sconosciuto o un'archiviazione che non risponde. */
export function readAudioChoice(storage: Storage | null): AudioChoice {
  return parseChoice(rawChoice(storage));
}

/** Ricorda la scelta; `false` se il browser non la tiene (allora vale finché la pagina resta aperta). */
export function writeAudioChoice(storage: Storage | null, choice: AudioChoice): boolean {
  if (!storage) return false;
  try {
    storage.setItem(AUDIO_CHOICE_KEY, JSON.stringify({ input: choice.input, output: choice.output }));
    return true;
  } catch {
    return false;
  }
}

export function sameAudioChoice(left: AudioChoice, right: AudioChoice): boolean {
  return left.input === right.input && left.output === right.output;
}

// La scelta di adesso: quella dell'archiviazione (letta di nuovo a ogni richiesta, così vale anche quella scritta da un
// ricaricamento), oppure quella che il browser non ha voluto tenere. L'oggetto resta lo stesso finché il valore non cambia,
// come vuole `useSyncExternalStore`.
let cache: { raw: string | null; choice: AudioChoice } = { raw: null, choice: SYSTEM_AUDIO };
let unsaved: AudioChoice | null = null;
const listeners = new Set<() => void>();

export function currentAudioChoice(): AudioChoice {
  if (unsaved) return unsaved;
  const raw = rawChoice(deviceStorage());
  if (raw !== cache.raw) cache = { raw, choice: parseChoice(raw) };
  return cache.choice;
}

/** Sceglie microfono e ascolto su questo computer e lo dice a chi ascolta (la postazione, la chiamata in corso). */
export function chooseAudio(choice: AudioChoice): void {
  unsaved = writeAudioChoice(deviceStorage(), choice) ? null : { ...choice };
  for (const listener of listeners) listener();
}

// Una scelta fatta in un'altra scheda del browser: il browser lo dice con l'evento `storage` (chiave nulla quando
// l'archiviazione si svuota), e chi ascolta qui, anche la chiamata in corso, la segue come se fosse fatta qui.
function onStorage(event: StorageEvent): void {
  if (event.key !== null && event.key !== AUDIO_CHOICE_KEY) return;
  for (const listener of listeners) listener();
}

/** Ascolta i cambi della scelta, fatti qui o in un'altra scheda; restituisce la funzione che smette. */
export function subscribeAudioChoice(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

/** La scelta di adesso, che segue i cambi. */
export function useAudioChoice(): AudioChoice {
  return useSyncExternalStore(subscribeAudioChoice, currentAudioChoice);
}

/** Se il nome sembra quello del microfono del computer (`BUILT_IN_MICROPHONE_WORDS`). */
export function looksBuiltIn(label: string): boolean {
  const lower = label.toLowerCase();
  return BUILT_IN_MICROPHONE_WORDS.some((word) => lower.includes(word));
}

/**
 * Il nome di un dispositivo da mostrare: senza il «Default - » (o «Predefinito - ») che Chrome mette davanti alla voce di
 * sistema e senza il codice USB in fondo, `(0b0e:24d1)`. Vuoto se il browser non dice il nome (prima del permesso).
 */
export function deviceName(device: { deviceId: string; label: string }): string {
  let name = device.label.trim();
  if (SYSTEM_DEVICE_IDS.has(device.deviceId)) name = name.replace(/^[^-]*\s-\s+/, "");
  return name.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)$/i, "").trim();
}

/** Il nome accorciato per il pulsante della postazione, con «…» in fondo. */
export function shortDeviceName(name: string): string {
  return name.length <= SHORT_NAME_LENGTH ? name : `${name.slice(0, SHORT_NAME_LENGTH - 1).trimEnd()}…`;
}

/** Se il browser sa mandare l'audio a un dispositivo scelto (Chrome sì, Safari no): senza, la tendina «Ascolto» non c'è. */
export function outputSelectionSupported(): boolean {
  return typeof HTMLMediaElement !== "undefined" && typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === "function";
}
